import Constant from '@/common/constant/Constant';
import KboardConstant from '@/common/constant/KboardConstant';
import Logger from '@/common/utils/Logger';
import Utils from '@/common/utils/utils';

/**
 * KBoard Connect Service
 * Manages cross-tab communication between sdesk and kboard via window.postMessage API.
 *
 * Flow:
 * 1. sdesk opens kboard in a new tab with URL: {kboard_domain}/mypage
 * 2. sdesk sends org/project info to kboard tab via postMessage
 * 3. kboard receives info, stores in sessionStorage, processes business logic
 * 4. kboard sends result back to sdesk via postMessage
 * 5. sdesk listens and triggers logic/API calls based on received data
 */

// Module-level state
let _kboardTab = null;
let _connectionStatus = KboardConstant.CONNECTION_STATUS.DISCONNECTED;
let _messageListener = null;
let _onResultCallback = null;
let _onStatusChangeCallback = null;
let _onSheetKnowledgeRequest = null;
let _retryCount = 0;
let _beforeUnloadListener = null;

/**
 * Get the kboard domain URL from environment or constant configuration.
 * @returns {string} kboard domain URL
 */
const getKboardDomain = () => {
    // Priority: env variable > constant
    return process.env.REACT_APP_KBOARD_DOMAIN || Constant.KBOARD_CONNECT?.DOMAIN || '';
};

/**
 * Open kboard in a new tab and initiate connection.
 * @param {Object} params
 * @param {Object} params.organizationDetail - { id, name }
 * @param {Object} params.projectDetail - { id, name }
 * @param {Array} params.promptKnowledgeList - K-Board prompt knowledge list available for the connected project
 * @param {Function} params.onResult - Callback when kboard sends result back
 * @param {Function} params.onStatusChange - Callback when connection status changes
 * @param {Function} params.onSheetKnowledgeRequest - Callback when kboard requests knowledge for a sheet: ({ sheetId, sheetName }) => { knowledgeMeta, knowledgeUrl }
 * @returns {boolean} Whether the tab was successfully opened
 */
const openKboardConnection = ({ organizationDetail, projectDetail, promptKnowledgeList = [],aiThreadChatId, onResult, onStatusChange, onSheetKnowledgeRequest }) => {
    const kboardDomain = getKboardDomain();
    if (!kboardDomain) {
        Logger.error('[KBoardConnect] kboard domain is not configured.');
        return false;
    }

    _onResultCallback = onResult || null;
    _onStatusChangeCallback = onStatusChange || null;
    _onSheetKnowledgeRequest = onSheetKnowledgeRequest || null;
    // If tab already open: send disconnect event to existing tab (let it handle UI),
    // then close our tracking reference and open a fresh connection.
    if (_kboardTab && !_kboardTab.closed) {
        _sendDisconnectNotice();
        // Do NOT close the tab — kboard tab stays open, only its connection state is reset.
        // Clear our side's reference so we treat this as a fresh connect.
        _removeMessageListener();
        _removeBeforeUnloadListener();
        _kboardTab = null;
        _retryCount = 0;
    }

    // Update status
    _updateStatus(KboardConstant.CONNECTION_STATUS.CONNECTING);

    // Open kboard in new tab
    const kboardUrl = `${kboardDomain}${KboardConstant.TAB_PATH}`;
    _kboardTab = window.open(kboardUrl, '_blank');

    if (!_kboardTab) {
        Logger.error('[KBoardConnect] Failed to open kboard tab. Popup might be blocked.');
        _updateStatus(KboardConstant.CONNECTION_STATUS.DISCONNECTED);
        return false;
    }

    // Start listening for messages from kboard
    _startMessageListener({ organizationDetail, projectDetail, promptKnowledgeList, aiThreadChatId });

    // Register beforeunload to notify kboard when sdesk page is refreshed/closed
    _registerBeforeUnloadListener();

    // Retry sending init message (kboard needs time to load)
    _retryCount = 0;
    _retrySendInit({ organizationDetail, projectDetail, promptKnowledgeList, aiThreadChatId });

    return true;
};

/**
 * Send context data to kboard tab.
 * @param {Object} params
 * @param {Object} params.organizationDetail - { id, name }
 * @param {Object} params.projectDetail - { id, name }
 * @param {Array} params.promptKnowledgeList - K-Board prompt knowledge list available for the connected project
 */
const sendContextToKboard = ({ organizationDetail, projectDetail, promptKnowledgeList = [] , aiThreadChatId}) => {
    if (!_kboardTab || _kboardTab.closed) {
        Logger.warn('[KBoardConnect] Cannot send context - kboard tab is closed.');
        _updateStatus(KboardConstant.CONNECTION_STATUS.DISCONNECTED);
        return;
    }

    const kboardDomain = getKboardDomain();
    const message = {
        type: KboardConstant.MESSAGE_TYPE.SDESK_SEND_CONTEXT,
        source: KboardConstant.MESSAGE_SOURCE.SDESK,
        payload: {
            organizationId: organizationDetail?.id || '',
            organizationName: organizationDetail?.name || '',
            projectId: projectDetail?.id || '',
            isEnableUsingKBoard: true,
            promptKnowledgeList,
            projectName:
                Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, projectDetail?.name) || '',
            aiThreadChatId: aiThreadChatId || '',
            timestamp: Date.now()
        }
    };

    try {
        _kboardTab.postMessage(message, kboardDomain);
        Logger.info('[KBoardConnect] Context sent to kboard:', message.payload);
    } catch (error) {
        Logger.error('[KBoardConnect] Failed to send context to kboard:', error);
    }
};

/**
 * Send sheet knowledge info to kboard in response to KBOARD_REQUEST_SHEET_KNOWLEDGE.
 * @param {Object} params
 * @param {string|number} params.sheetId - The sheet ID the knowledge relates to
 * @param {string} params.knowledgeMeta - Knowledge registration timestamp/label
 * @param {string} params.knowledgeUrl - Knowledge URL to display
 */
const sendSheetKnowledgeToKboard = (params) => {
    let { sheetId, knowledgeMeta, knowledgeUrl } = params
    if (!_kboardTab || _kboardTab.closed) {
        Logger.warn('[KBoardConnect] Cannot send sheet knowledge - kboard tab is closed.');
        return;
    }

    const kboardDomain = getKboardDomain();
    const message = {
        type: KboardConstant.MESSAGE_TYPE.SDESK_SEND_SHEET_KNOWLEDGE,
        source: KboardConstant.MESSAGE_SOURCE.SDESK,
        payload: {
            sheetId: sheetId || '',
            knowledgeMeta: knowledgeMeta || '',
            knowledgeUrl: knowledgeUrl || '',
            timestamp: Date.now(),
        },
    };

    try {
        _kboardTab.postMessage(message, kboardDomain);
        Logger.info('[KBoardConnect] Sheet knowledge sent to kboard:', message.payload);
    } catch (error) {
        Logger.error('[KBoardConnect] Failed to send sheet knowledge to kboard:', error);
    }
};

/**
 * Send updated aiThreadChatId to kboard so it updates its sdeskContext.
 * Called when sdesk creates a new chat thread (e.g., when the previous tab was closed).
 *
 * @param {string} newThreadId - The newly created aiThreadChatId
 */
const sendUpdatedThreadIdToKboard = (newThreadId) => {
    if (!_kboardTab || _kboardTab.closed) {
        Logger.warn('[KBoardConnect] Cannot send updated thread ID - kboard tab is closed.');
        return;
    }

    if (!newThreadId) return;

    const kboardDomain = getKboardDomain();
    const message = {
        type: KboardConstant.MESSAGE_TYPE.SDESK_UPDATE_THREAD_ID,
        source: KboardConstant.MESSAGE_SOURCE.SDESK,
        payload: {
            aiThreadChatId: newThreadId,
            timestamp: Date.now(),
        },
    };

    try {
        _kboardTab.postMessage(message, kboardDomain);
        Logger.info('[KBoardConnect] Updated thread ID sent to kboard:', newThreadId);
    } catch (error) {
        Logger.error('[KBoardConnect] Failed to send updated thread ID to kboard:', error);
    }
};

/**
 * Close the kboard connection and cleanup.
 */
const closeKboardConnection = () => {
    _sendDisconnectNotice();
    _removeMessageListener();
    _removeBeforeUnloadListener();

    _kboardTab = null;
    _onResultCallback = null;
    _onStatusChangeCallback = null;
    _onSheetKnowledgeRequest = null;
    _retryCount = 0;
    _updateStatus(KboardConstant.CONNECTION_STATUS.DISCONNECTED);

    Logger.info('[KBoardConnect] Connection closed.');
};

/**
 * Get current connection status.
 * @returns {string} Current status from KBOARD_CONNECT_STATUS
 */
const getConnectionStatus = () => {
    // Check if tab was closed externally
    if (_connectionStatus !== KboardConstant.CONNECTION_STATUS.DISCONNECTED && _kboardTab?.closed) {
        _updateStatus(KboardConstant.CONNECTION_STATUS.DISCONNECTED);
    }
    return _connectionStatus;
};

/**
 * Check if kboard tab is still open.
 * @returns {boolean}
 */
const isKboardTabOpen = () => {
    return _kboardTab && !_kboardTab.closed;
};

// ======= Private helpers =======

const _updateStatus = newStatus => {
    _connectionStatus = newStatus;
    _onStatusChangeCallback?.(newStatus);
};

const _startMessageListener = ({ organizationDetail, projectDetail, promptKnowledgeList = [] , aiThreadChatId}) => {
    _removeMessageListener();

    _messageListener = event => {
        const kboardDomain = getKboardDomain();

        // Validate origin
        if (event.origin !== kboardDomain) return;

        const data = event.data;
        if (!data || data.source !== KboardConstant.MESSAGE_SOURCE.KBOARD) return;

        switch (data.type) {
            case KboardConstant.MESSAGE_TYPE.KBOARD_CONNECTION_READY:
                Logger.info('[KBoardConnect] Kboard is ready. Sending context...');
                _updateStatus(KboardConstant.CONNECTION_STATUS.CONNECTED);
                sendContextToKboard({ organizationDetail, projectDetail, promptKnowledgeList, aiThreadChatId });
                break;

            case KboardConstant.MESSAGE_TYPE.KBOARD_SEND_RESULT:
                Logger.info('[KBoardConnect] Received result from kboard:', data.payload);
                _onResultCallback?.(data.payload);
                break;

            case KboardConstant.MESSAGE_TYPE.KBOARD_CONNECTION_CLOSED:
                Logger.info('[KBoardConnect] Kboard connection closed by kboard side.');
                _updateStatus(KboardConstant.CONNECTION_STATUS.DISCONNECTED);
                break;

            case KboardConstant.MESSAGE_TYPE.KBOARD_REQUEST_SHEET_KNOWLEDGE: {
                Logger.info('[KBoardConnect] Kboard requested sheet knowledge:', data.payload);
                if (_onSheetKnowledgeRequest) {
                    // Caller resolves knowledge data (sync or async) and we send the response
                    const result = _onSheetKnowledgeRequest(data.payload);
                    Promise.resolve(result).then(knowledgeData => {
                        sendSheetKnowledgeToKboard({
                            sheetId: data.payload?.sheetId || '',
                            knowledgeMeta: knowledgeData?.knowledgeMeta || '',
                            knowledgeUrl: knowledgeData?.knowledgeUrl || '',
                        });
                    }).catch(error => {
                        Logger.error('[KBoardConnect] onSheetKnowledgeRequest callback error:', error);
                    });
                }
                break;
            }

            default:
                break;
        }
    };

    window.addEventListener('message', _messageListener);
};

const _removeMessageListener = () => {
    if (_messageListener) {
        window.removeEventListener('message', _messageListener);
        _messageListener = null;
    }
};

const _retrySendInit = ({ organizationDetail, projectDetail, promptKnowledgeList = [] , aiThreadChatId}) => {
    if (_retryCount >= KboardConstant.INIT_RETRY.MAX_COUNT) {
        Logger.warn('[KBoardConnect] Max retry reached. Kboard may not be responding.');
        return;
    }

    if (!_kboardTab || _kboardTab.closed) {
        _updateStatus(KboardConstant.CONNECTION_STATUS.DISCONNECTED);
        return;
    }

    // If already connected via KBOARD_CONNECTION_READY, stop retrying
    if (_connectionStatus === KboardConstant.CONNECTION_STATUS.CONNECTED) return;

    const kboardDomain = getKboardDomain();
    const initMessage = {
        type: KboardConstant.MESSAGE_TYPE.SDESK_INIT_CONNECTION,
        source: KboardConstant.MESSAGE_SOURCE.SDESK,
        payload: {
            organizationId: organizationDetail?.id || '',
            organizationName: organizationDetail?.name || '',
            projectId: projectDetail?.id || '',
            projectName: projectDetail?.name || '',
            promptKnowledgeList,
            aiThreadChatId: aiThreadChatId || '',
            timestamp: Date.now()
        }
    };

    try {
        _kboardTab.postMessage(initMessage, kboardDomain);
    } catch (error) {
        // Tab might not be ready yet, will retry
    }

    _retryCount++;
    setTimeout(() => {
        _retrySendInit({ organizationDetail, projectDetail, promptKnowledgeList, aiThreadChatId });
    }, KboardConstant.INIT_RETRY.INTERVAL_MS);
};

/**
 * Send SDESK_DISCONNECT to the existing kboard tab so it can reset its state.
 * The tab itself is NOT closed — only its connection state is reset.
 */
const _sendDisconnectNotice = () => {
    if (!_kboardTab || _kboardTab.closed) return;
    const kboardDomain = getKboardDomain();
    const message = {
        type: KboardConstant.MESSAGE_TYPE.SDESK_DISCONNECT,
        source: KboardConstant.MESSAGE_SOURCE.SDESK,
        payload: { timestamp: Date.now() },
    };
    try {
        _kboardTab.postMessage(message, kboardDomain);
        Logger.info('[KBoardConnect] Sent SDESK_DISCONNECT to kboard tab.');
    } catch (error) {
        // Tab may already be navigating away — ignore
    }
};

/**
 * Register a beforeunload listener so kboard is notified when sdesk refreshes/closes.
 */
const _registerBeforeUnloadListener = () => {
    _removeBeforeUnloadListener();
    _beforeUnloadListener = () => {
        _sendDisconnectNotice();
    };
    window.addEventListener('beforeunload', _beforeUnloadListener);
};

/**
 * Remove the beforeunload listener.
 */
const _removeBeforeUnloadListener = () => {
    if (_beforeUnloadListener) {
        window.removeEventListener('beforeunload', _beforeUnloadListener);
        _beforeUnloadListener = null;
    }
};

export {
    openKboardConnection,
    sendContextToKboard,
    sendSheetKnowledgeToKboard,
    sendUpdatedThreadIdToKboard,
    closeKboardConnection,
    getConnectionStatus,
    isKboardTabOpen,
    getKboardDomain
};
